import React, { useEffect, useMemo, useState } from 'react'
import RegionOverlay, { DisplayRegion } from './RegionOverlay'

export type PdfRegion = {
  id: string
  pageNumber: number
  x1: number
  y1: number
  x2: number
  y2: number
  validated?: boolean
}

type OverlayRect = {
  left: number
  top: number
  width: number
  height: number
}

type Props = {
  enabled: boolean
  pageNumber: number
  pdfViewerProxy: any
  regions: PdfRegion[]
  readOnly?: boolean
  onRegionsChange: (regions: PdfRegion[]) => void
}

const getPageView = (proxy: any, pageNumber: number) => {
  const viewerRef = proxy?.getViewerRef?.()
  return viewerRef?.pdfViewer?._pages?.[Math.max(0, pageNumber - 1)]
}

const PdfReactAnnotator: React.FC<Props> = ({
  enabled,
  pageNumber,
  pdfViewerProxy,
  regions,
  readOnly = false,
  onRegionsChange,
}) => {
  const [overlayRect, setOverlayRect] = useState<OverlayRect | null>(null)

  useEffect(() => {
    if (!enabled) {
      setOverlayRect(null)
      return
    }

    const applyOverlayFromPayload = (payload?: {
      currentPageNumber?: number
      pageRect?: { left: number; top: number; width: number; height: number } | null
      canvasRect?: { left: number; top: number; width: number; height: number } | null
    }) => {
      const host = document.getElementById('pdf-viewer')
      const iframe = document.getElementById('pdfViewerContainer') as HTMLIFrameElement | null
      if (!host || !iframe || !iframe.contentWindow) {
        setOverlayRect(null)
        return
      }

      const incomingPageNumber = payload?.currentPageNumber
      if (incomingPageNumber && incomingPageNumber !== pageNumber) {
        return
      }

      const targetRect = payload?.canvasRect || payload?.pageRect
      if (!targetRect) {
        return
      }

      const hostRect = host.getBoundingClientRect()
      const iframeRect = iframe.getBoundingClientRect()
      setOverlayRect({
        // canvas/page rect is measured inside iframe viewport coordinates.
        // Convert to parent-space by adding iframe origin, then to host-local.
        left: iframeRect.left - hostRect.left + targetRect.left,
        top: iframeRect.top - hostRect.top + targetRect.top,
        width: targetRect.width,
        height: targetRect.height,
      })
    }

    const updateOverlayFromViewer = () => {
      const host = document.getElementById('pdf-viewer')
      const iframe = document.getElementById('pdfViewerContainer') as HTMLIFrameElement | null
      if (!host || !iframe) {
        return
      }

      const pageView = getPageView(pdfViewerProxy, pageNumber)
      const pageDiv = pageView?.div as HTMLElement | undefined
      const pageCanvas = (pageView?.canvas as HTMLElement | undefined) || pageDiv?.querySelector?.('canvas') as HTMLElement | undefined
      const target = pageCanvas || pageDiv
      if (!target) {
        return
      }

      const hostRect = host.getBoundingClientRect()
      const iframeRect = iframe.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()

      if (!targetRect.width || !targetRect.height) {
        return
      }

      setOverlayRect({
        left: iframeRect.left - hostRect.left + targetRect.left,
        top: iframeRect.top - hostRect.top + targetRect.top,
        width: targetRect.width,
        height: targetRect.height,
      })
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event?.data as {
        source?: string
        type?: string
        payload?: {
          currentPageNumber?: number
          pageRect?: { left: number; top: number; width: number; height: number } | null
          canvasRect?: { left: number; top: number; width: number; height: number } | null
        }
      } | undefined
      if (!data || data.source !== 'sod-pdf-viewer') return
      if (!data.type) return
      applyOverlayFromPayload(data.payload)
    }

    const requestViewerState = () => {
      const iframe = document.getElementById('pdfViewerContainer') as HTMLIFrameElement | null
      iframe?.contentWindow?.postMessage(
        {
          source: 'sod-parent',
          type: 'SOD_PDF_REQUEST_STATE',
        },
        '*',
      )
    }

    const handleResize = () => {
      updateOverlayFromViewer()
      requestViewerState()
    }

    updateOverlayFromViewer()
    requestViewerState()
    const timer = window.setInterval(handleResize, 250)
    window.addEventListener('resize', handleResize)
    window.addEventListener('message', handleMessage)

    return () => {
      window.clearInterval(timer)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('message', handleMessage)
    }
  }, [enabled, pageNumber, pdfViewerProxy])

  const displayedRegions = useMemo<DisplayRegion[]>(() => {
    if (!overlayRect) return []
    const pageView = getPageView(pdfViewerProxy, pageNumber)
    if (!pageView?.viewport) return []

    const scaleX = overlayRect.width / pageView.viewport.width
    const scaleY = overlayRect.height / pageView.viewport.height

    return regions
      .filter((region) => region.pageNumber === pageNumber)
      .map((region) => {
        const start = pageView.viewport.convertToViewportPoint(region.x1, region.y1)
        const end = pageView.viewport.convertToViewportPoint(region.x2, region.y2)
        const left = Math.min(start[0], end[0]) * scaleX
        const top = Math.min(start[1], end[1]) * scaleY
        const width = Math.abs(end[0] - start[0]) * scaleX
        const height = Math.abs(end[1] - start[1]) * scaleY
        return { id: region.id, left, top, width, height, validated: region.validated }
      })
  }, [overlayRect, pageNumber, pdfViewerProxy, regions])

  const handleDisplayRegionsChange = (displayRegions: DisplayRegion[]) => {
    if (!overlayRect) {
      return
    }

    const pageView = getPageView(pdfViewerProxy, pageNumber)
    if (!pageView?.viewport) {
      return
    }

    const scaleX = pageView.viewport.width / overlayRect.width
    const scaleY = pageView.viewport.height / overlayRect.height

    const otherPageRegions = regions.filter((item) => item.pageNumber !== pageNumber)
    const convertedCurrentPage: PdfRegion[] = displayRegions.map((draw) => {
      const start = pageView.viewport.convertToPdfPoint(
        draw.left * scaleX,
        draw.top * scaleY
      )
      const end = pageView.viewport.convertToPdfPoint(
        (draw.left + draw.width) * scaleX,
        (draw.top + draw.height) * scaleY
      )

      // In PDF Space, origin (Y=0) is at the bottom.
      // Top-left of the box = min X, max Y
      // Bottom-right of the box = max X, min Y
      const x1 = Math.min(start[0], end[0]) // topLeftX
      const y1 = Math.max(start[1], end[1]) // topLeftY (highest Y in PDF space)
      const x2 = Math.max(start[0], end[0]) // bottomRightX
      const y2 = Math.min(start[1], end[1]) // bottomRightY (lowest Y in PDF space)

      return {
        id: draw.id,
        pageNumber,
        x1,
        y1,
        x2,
        y2,
        validated: draw.validated,
      }
    })

    onRegionsChange([...otherPageRegions, ...convertedCurrentPage])
  }

  const debugPanel = import.meta.env.DEV ? (
    <div
      id="sod-pdf-debug-panel"
      style={{
        position: 'fixed',
        top: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.82)',
        color: '#fff',
        padding: '5px 20px',
        fontSize: '12px',
        lineHeight: '1.6',
        pointerEvents: 'none',
        zIndex: 10001,
        borderRadius: '6px',
        whiteSpace: 'nowrap',
      }}
    >
      Hover to see coordinates...
    </div>
  ) : null

  if (!enabled || !overlayRect) return debugPanel

  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: `${overlayRect.left}px`,
          top: `${overlayRect.top}px`,
          width: `${overlayRect.width}px`,
          height: `${overlayRect.height}px`,
          zIndex: 999,
          overflow: 'hidden',
          pointerEvents: readOnly ? 'none' : 'auto',
        }}
        onWheel={(event) => {
          if (readOnly) return

          const iframe = document.getElementById('pdfViewerContainer') as HTMLIFrameElement | null
          const iframeDoc = iframe?.contentDocument
          const iframeWin = iframe?.contentWindow
          if (!iframeDoc && !iframeWin) return

          const scroller =
            (iframeDoc?.getElementById('viewerContainer') as HTMLElement | null) ||
            (iframeDoc?.getElementById('outerContainer') as HTMLElement | null) ||
            (iframeDoc?.scrollingElement as HTMLElement | null)

          if (scroller) {
            event.preventDefault()
            scroller.scrollLeft += event.deltaX
            scroller.scrollTop += event.deltaY
            return
          }

          if (iframeWin) {
            event.preventDefault()
            iframeWin.scrollBy({ left: event.deltaX, top: event.deltaY, behavior: 'auto' })
          }
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const pxLeft = e.clientX - rect.left
          const pxTop = e.clientY - rect.top

          if (import.meta.env.DEV) {
            const pageView = getPageView(pdfViewerProxy, pageNumber)
            if (pageView?.viewport) {
              const scaleX = pageView.viewport.width / overlayRect.width
              const scaleY = pageView.viewport.height / overlayRect.height
              const pdfPoint = pageView.viewport.convertToPdfPoint(pxLeft * scaleX, pxTop * scaleY)
              const debugEl = document.getElementById('sod-pdf-debug-panel')
              if (debugEl) {
                debugEl.innerHTML = `<div style="display:grid;grid-template-columns:auto 1fr auto 1fr;gap:2px 20px;align-items:baseline"><span style="color:#9ca3af">UI px</span><span>X: ${pxLeft.toFixed(1)}, Y: ${pxTop.toFixed(1)}</span><span style="color:#9ca3af">Overlay</span><span>${overlayRect.width} × ${overlayRect.height}</span><span style="color:#9ca3af">Viewport</span><span>${pageView.viewport.width.toFixed(0)} × ${pageView.viewport.height.toFixed(0)}</span><span style="color:#9ca3af">PDF space</span><span>X: ${pdfPoint[0].toFixed(1)}, Y: ${pdfPoint[1].toFixed(1)}</span></div>`
              }
            }
          }
        }}
      >
        <RegionOverlay
          enabled={enabled}
          readOnly={readOnly}
          width={overlayRect.width}
          height={overlayRect.height}
          regions={displayedRegions}
          onRegionsChange={handleDisplayRegionsChange}
        />
      </div>
      {debugPanel}
    </>
  )
}

export default PdfReactAnnotator
